/**
 * EngineResultView Component
 *
 * Unified view for displaying engine run results with tabbed interface.
 * Tabs:
 * 1. Summary → EngineSummary
 * 2. Detail → EngineDetail
 * 3. Cost → CostSectionInternal (if showInternalTabs)
 * 4. Log → LogEntriesPanelInternal (if showInternalTabs)
 * 5. Object → FullObjectPanelInternal (if showInternalTabs)
 */

import { useState, useMemo } from "react";
import type { SubengineRunResult, AsoEngineLogEntry } from "@a7y/types";
import { DevCardBadge } from "../shared/DevCardBadge";
import { EngineSummary } from "../shared/EngineSummary";
import { EngineDetail } from "../shared/EngineDetail";
import {
  CostSectionInternal,
  LogEntriesPanelInternal,
  FullObjectPanelInternal,
  ENGINE_DISPLAY_LABELS,
  type EngineLogEntry,
} from "../shared";

export type EngineResultTabId = "summary" | "detail" | "cost" | "log" | "object";

export interface EngineResultViewProps {
  /** Full subengine run result */
  result: SubengineRunResult<unknown, unknown, unknown>;
  /** Whether to show internal/debug tabs (Cost, Log, Object) */
  showInternalTabs?: boolean;
  /** Default tab to show */
  defaultTab?: EngineResultTabId;
  /** Additional CSS classes */
  className?: string;
}

interface TabConfig {
  id: EngineResultTabId;
  label: string;
}

/**
 * Converts AsoEngineLogEntry[] to EngineLogEntry[] format
 */
function convertLogEntries(log: AsoEngineLogEntry[]): EngineLogEntry[] {
  return log.map((entry) => ({
    timestamp: entry.timestamp instanceof Date ? entry.timestamp.toISOString() : String(entry.timestamp),
    action: entry.action,
  }));
}

export function EngineResultView({
  result,
  showInternalTabs = false,
  defaultTab,
  className = "",
}: EngineResultViewProps) {
  // Normalize legacy output formats
  // Some engines return { unified_output: SubengineOutput } instead of { output: SubengineOutput }
  // This handles both the standard SubengineRunResult and legacy formats
  const normalizedResult = useMemo(() => {
    // If result already has the standard structure, use it
    if (result.output?.summary) {
      return result;
    }

    // Check for legacy format where unified_output is nested inside output
    const legacyOutput = result.output as unknown as Record<string, unknown> | undefined;
    if (legacyOutput?.unified_output) {
      return {
        ...result,
        output: legacyOutput.unified_output as typeof result.output,
      };
    }

    // Check for legacy format at top level (result itself has unified_output)
    const resultAsLegacy = result as unknown as Record<string, unknown>;
    if (resultAsLegacy.unified_output) {
      return {
        ...result,
        output: resultAsLegacy.unified_output as typeof result.output,
      };
    }

    // Return as-is if no normalization needed
    return result;
  }, [result]);

  const { output, engine_name, cost, log } = normalizedResult;

  // Safely access output properties with null checks
  const summary = output?.summary;
  const effectiveEngineName = engine_name;

  // Build tab list
  const tabs = useMemo(() => {
    const tabList: TabConfig[] = [
      { id: "summary", label: "Summary" },
      { id: "detail", label: "Detail" },
    ];

    if (showInternalTabs) {
      tabList.push(
        { id: "cost", label: "Cost" },
        { id: "log", label: "Log" },
        { id: "object", label: "Object" }
      );
    }

    return tabList;
  }, [showInternalTabs]);

  const resolvedDefaultTab = defaultTab ?? "summary";
  const [activeTab, setActiveTab] = useState<EngineResultTabId>(resolvedDefaultTab);

  // Ensure active tab is valid
  const effectiveTab = useMemo(() => {
    if (!showInternalTabs && (activeTab === "cost" || activeTab === "log" || activeTab === "object")) {
      return "summary";
    }
    return activeTab;
  }, [activeTab, showInternalTabs]);

  // Convert log entries to the expected format
  const formattedLog = log ? convertLogEntries(log) : undefined;

  const displayLabel = ENGINE_DISPLAY_LABELS[effectiveEngineName] ?? effectiveEngineName;

  return (
    <DevCardBadge name="EngineResultView">
    <div className={className}>
      {/* Header with engine info */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{displayLabel}</h2>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {result.fromCache && (
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
              Cached
            </span>
          )}
          <span>v{result.engine_version}</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-4 border-b border-gray-200 dark:border-zinc-700">
        <nav className="flex space-x-4" aria-label="Engine result tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                effectiveTab === tab.id
                  ? "border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-zinc-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pt-2">
        {effectiveTab === "summary" && (
          summary ? (
            <EngineSummary summary={summary} engineName={effectiveEngineName} />
          ) : (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Summary data is not available for this engine result.
              </p>
            </div>
          )
        )}

        {effectiveTab === "detail" && output && (
          <EngineDetail output={output} engineType={effectiveEngineName} />
        )}

        {effectiveTab === "cost" && showInternalTabs && (
          <div className="space-y-4">
            {cost ? (
              <CostSectionInternal cost={cost} />
            ) : (
              <div className="text-gray-500 dark:text-gray-400 italic p-4 bg-gray-50 dark:bg-zinc-800 rounded-lg">
                No cost data available
              </div>
            )}
          </div>
        )}

        {effectiveTab === "log" && showInternalTabs && (
          <LogEntriesPanelInternal log={formattedLog} />
        )}

        {effectiveTab === "object" && showInternalTabs && (
          <FullObjectPanelInternal data={result} />
        )}
      </div>
    </div>
    </DevCardBadge>
  );
}
